import { getConfig } from "../config";

/**
 * Per-environment Entra group gate for CORP users.
 *
 * A CORP/CORP2 user is not tied to one SSC — they roam across every tenant via the org
 * switcher, so "which environment may this corp user touch" cannot be answered from their
 * Graph companyName (corp-capable users share one short allow-list of company names, and it
 * is the same list in every environment) nor from
 * their WHOTO (that says CORP, not dev-vs-prod). It has to come from the environment.
 *
 * The answer is an Entra security group per environment, named in that environment's Key
 * Vault (`CorpAccessGroups` -> `window.config.corpAccessGroups`):
 *
 *   dev + QA vaults                    -> the object id of CSM-NP-QA-Swg-access
 *   prod + Asia prod + Emea prod       -> the object id of CSM-P-Swg-access
 *
 * Keeping the ids in the vault rather than in source is what lets ONE Entra app
 * registration serve every environment and still admit a different set of corp users to
 * each — an "Assignment required" flag on the app registration could not do that, and
 * would also have to enumerate every SSC operator group.
 *
 * WHAT THIS FILE IS NOT
 * ---------------------
 * This is the client half, and the client is a UX gate, not the security boundary (the
 * auth cache lives in user-writable sessionStorage). The Function App enforces the same
 * group ids on every request: `api/users/current` refuses a non-member outright, and
 * SafeAmbient refuses them the cross-tenant impersonation override. See
 * Security/CorpAccessPolicy.cs in the Function App repo.
 */

/** Values like `__CORP_ACCESS_GROUPS__` — the token shape the pipeline replaces at build. */
const UNREPLACED_TOKEN = /^__[A-Z0-9_]+__$/;

/**
 * Microsoft Graph checkMemberGroups accepts at most 20 group ids per request and fails the
 * WHOLE request if the collection is invalid, so an over-long list must be truncated rather
 * than sent. One group per environment is the expected configuration; this only exists so a
 * mis-edited secret degrades predictably.
 */
export const MAX_CHECKED_GROUPS = 20;

/**
 * The group object ids required in THIS environment, canonicalized to lower case.
 *
 * Accepts comma, semicolon or whitespace separation so the secret can be pasted from the
 * portal in any of the usual shapes. An unreplaced build token (local dev, or an
 * environment whose vault has no `CorpAccessGroups` secret) yields an empty list, which
 * means "no gate" — see `isCorpGroupGateActive`.
 */
export function configuredCorpAccessGroups(): string[] {
  const raw = (getConfig()?.corpAccessGroups ?? "").trim();

  if (!raw || UNREPLACED_TOKEN.test(raw)) return [];

  return raw
    .split(/[\s,;]+/)
    .map((id) => id.trim().toLowerCase())
    .filter((id) => id.length > 0);
}

/**
 * Is the CORP group gate switched on in this environment?
 *
 * False (no ids configured) deliberately means "behave exactly as before" — the Swagelok
 * company allow-list alone licenses corp users. That follows the same rule as every other
 * option in this app: an absent setting must not change behaviour. The consequence worth
 * knowing is that a vault which never receives the secret leaves CORP access ungated in
 * that environment, which is why the Function App enforces it independently.
 */
export function isCorpGroupGateActive(): boolean {
  return configuredCorpAccessGroups().length > 0;
}

/**
 * Result of reading the ID token's `groups` claim.
 *
 * `indeterminate` is not a failure — it is the honest answer whenever the token cannot
 * settle the question, and the caller must then ask Microsoft Graph. Two real cases produce
 * it, and conflating either with "not a member" would lock out legitimate users:
 *
 *  - **Overage.** Entra caps the claim at 200 groups and replaces it with `hasgroups: true`
 *    (or `_claim_names`/`_claim_sources`) past that. Anyone in 200+ groups — routine for a
 *    long-tenured employee — has no `groups` array at all.
 *  - **The optional claim isn't configured yet.** Until the `groups` optional claim is added
 *    to the app registration's ID token, NO user has the claim. Treating that as "not a
 *    member" would deny every corp user the moment this code ships; treating it as "member"
 *    would silently disable the gate. Asking Graph is the only correct response.
 */
export type GroupClaimVerdict = "member" | "not-member" | "indeterminate";

/** The subset of OIDC ID-token claims this decision reads. */
export interface GroupClaims {
  groups?: unknown;
  hasgroups?: unknown;
  _claim_names?: unknown;
  _claim_sources?: unknown;
}

/**
 * Decide membership from the ID token alone, with no network call.
 *
 * `required` is expected to come from `configuredCorpAccessGroups()` (already lower-cased);
 * claim values are lower-cased here because Entra's casing of GUIDs is not guaranteed to
 * match whatever was pasted into the vault.
 */
export function verdictFromClaims(
  claims: GroupClaims | null | undefined,
  required: string[]
): GroupClaimVerdict {
  if (required.length === 0) return "member"; // gate inactive; nothing to prove

  if (!claims) return "indeterminate";

  // Overage markers win over an absent array: the claim was suppressed, not empty.
  if (claims.hasgroups === true || claims.hasgroups === "true") return "indeterminate";

  if (isRecord(claims._claim_names) && "groups" in claims._claim_names) {
    return "indeterminate";
  }

  const raw = claims.groups;

  // A single-group claim can arrive as a bare string rather than an array.
  const present =
    typeof raw === "string"
      ? [raw]
      : Array.isArray(raw)
      ? raw.filter((g): g is string => typeof g === "string")
      : null;

  // No claim at all -> the optional claim may simply not be configured. Ask Graph.
  if (present === null) return "indeterminate";

  const held = new Set(present.map((g) => g.trim().toLowerCase()));

  return required.some((id) => held.has(id)) ? "member" : "not-member";
}

/**
 * True when `matched` (what Graph confirmed the user is actually in) satisfies the gate.
 * Kept here next to `verdictFromClaims` so both paths share one notion of "satisfied".
 */
export function isMemberOfAny(matched: string[], required: string[]): boolean {
  if (required.length === 0) return true;

  const held = new Set(matched.map((g) => g.trim().toLowerCase()));

  return required.some((id) => held.has(id));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
